import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useEditor, BubbleMenu, EditorContent } from '@tiptap/react';
import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import BulletList from '@tiptap/extension-bullet-list';
import ListItem from '@tiptap/extension-list-item';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import History from '@tiptap/extension-history';
import { Color } from '@tiptap/extension-color';
import TextStyle from '@tiptap/extension-text-style';
import HardBreak from '@tiptap/extension-hard-break';
import './TextblockEditorScreen.css';
import '../styles/MainContainer.css'
import Icon from '../home/Icon';
import CustomDropdown from '../input/CustomDropdown';
import { useSuccessBar } from '../context/SuccessBarContext';
import { usePopup } from '../context/PopupContext';
import api from '../wrapper/API';
import TextblockEditorPlaceholder from './TextblockEditorPlaceholder';
import TextblockUsage from './TextblockUsage';
import Tooltip from '../tooltip/Tooltip';
import ActionButton from '../input/ActionButton';
import Popup from '../popup/Popup';
import PopupDescriptionItem from '../popup/PopupDescriptionItem';
import { SEND_SCOPE, SCOPE_LABELS, EDITABLE_SCOPES, normalizeScope } from '../../util/textblockScope';
import OfflineBanner from '../shell/OfflineBanner';
import Textfield from '../input/Textfield';
import TextblockVariantTabs from './TextblockVariantTabs';
import useLocations from '../../hooks/useLocations';
// Textblock and variant saves change which add-on placeholders are in use (catalog key lock / delete) — every
// successful save or delete marks the shared add-on catalog stale (refetched on its next load, no request here).
import { invalidateAddOns } from '../../hooks/useAddOns';
import { findTextblockVariant, locationsWithoutVariant, textblockVariants } from '../../util/textblockVariants';

// Tab key of the default text; variant tabs use the location id.
const DEFAULT_TAB = 'default';
// Returned by a guarded action that opened the next popup (the discard popup must not close it again).
const OPENS_POPUP = 'opensPopup';

// Placeholders follow the actual usages of a textblock (there are no default textblocks anymore):
// mail types (slots) of group/template rules that point to it (usage entries "<Gruppe|Vorlage>:
// <Slot-Label>") and template mail steps, which send announcements — to groups or to the newsletter
// (hence the unsubscribe link). No usage → null = general list.
function placeholderScope(slots, usage) {
  if (!usage) return { labels: [], keys: null };
  const served = new Map();
  const byLabel = new Map(slots.map(s => [s.label, s]));
  [...(usage.participantGroupRules ?? []), ...(usage.templateRules ?? [])].forEach(entry => {
    const slot = byLabel.get(entry.slice(entry.lastIndexOf(': ') + 2));
    if (slot) served.set(slot.slot, slot);
  });
  const usedInSteps = (usage.templateSteps ?? []).length > 0;
  const announcement = slots.find(s => s.slot === 'announcement');

  const keys = [...served.values()].flatMap(s => s.placeholders ?? []);
  if (usedInSteps) keys.push(...(announcement?.placeholders ?? []), '{NewsletterUnsubscribeLink}');
  const labels = [...served.values()].map(s => `„${s.label}“`);
  if (usedInSteps) labels.push('Mail-Schritte');
  return { labels, keys: keys.length > 0 ? [...new Set(keys)] : null };
}

// Axios error → text for the error popup. 400/409 carry a plain German message (e.g. the usages
// that block a delete); ProblemDetails carry it in "detail".
function apiErrorText(err, fallback) {
  const data = err?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data && typeof data.detail === 'string' && data.detail.trim()) return data.detail;
  return fallback;
}

function TextblockEditorScreen() {
  const {startSuccessBarLoading, startSuccessBarSuccess, closeSuccessBar} = useSuccessBar();
  const {openPopup, closePopup} = usePopup();

  // Aktuell ausgewählter Textbaustein als komplettes Objekt (oder null = "neu/keiner ausgewählt").
  // Backend liefert TextblockResponse mit camelCase: { id, name, frontendName, subject, body, language,
  // isSystemBlock, sendScope, variants: [{ locationId, locationName, subject, body }] }.
  const [selected, setSelected] = useState(null);
  const [selectedTextBlockName, setSelectedTextBlockName] = useState("");
  const [selectedTextBlockSubject, setSelectedTextBlockSubject] = useState("");
  const [selectedTextBlockFrontendName, setSelectedTextBlockFrontendName] = useState("");
  const [error, setError] = useState("");
  const [textblocks, setTextblocks] = useState([]);
  const [popupName, setPopupName] = useState("");
  const [popupFrontendName, setPopupFrontendName] = useState("");
  const [popupSubject, setPopupSubject] = useState("");
  const [popupSendScope, setPopupSendScope] = useState(SEND_SCOPE.Both);
  const [selectedSendScope, setSelectedSendScope] = useState(SEND_SCOPE.Both);

  // Mail slot catalog (labels, placeholders) — loaded once per visit.
  const [slots, setSlots] = useState([]);
  // "Verwendet in" per textblock id: { status: 'loading' | 'loaded' | 'failed', data }. Loaded when a
  // block is opened, at most once per block and visit.
  const [usageById, setUsageById] = useState({});
  const requestedUsageIds = useRef(new Set());

  // Location variants of the opened block: active tab (DEFAULT_TAB or the location id of a variant), an
  // unsaved "+ Variante" draft ({ locationId, locationName }) and the subject of the opened variant.
  const [activeTab, setActiveTab] = useState(DEFAULT_TAB);
  const [newVariant, setNewVariant] = useState(null);
  const [variantSubject, setVariantSubject] = useState("");
  const [pickerLocationId, setPickerLocationId] = useState("");
  // Remounts the textblock dropdown so it shows the open block again while a switch waits for "Verwerfen".
  const [dropdownKey, setDropdownKey] = useState(0);
  // Saved state of the editor content (as normalized by TipTap) and of the variant subject → unsaved changes.
  const bodyBaselineRef = useRef(null);
  const subjectBaselineRef = useRef("");
  // Action waiting for "Verwerfen" in the discard popup.
  const pendingActionRef = useRef(null);
  // Block + tab that are open — results of a variant save/delete only touch the editor while they still match.
  const openRef = useRef({ textblockId: null, tab: DEFAULT_TAB });
  openRef.current = { textblockId: selected?.id ?? null, tab: activeTab };
  // Locations for "+ Variante" — one fetch per visit, as soon as a block that can have variants is opened.
  const { locations, loaded: locationsLoaded, failed: locationsFailed, ensureLoaded: ensureLocations } = useLocations();
  const canHaveVariants = Boolean(selected && !selected.isSystemBlock);

  useEffect(() => {
    api.get('/Textblock/Slots')
      .then(res => setSlots(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, []);

  const loadUsage = useCallback((id) => {
    if (requestedUsageIds.current.has(id)) return;
    requestedUsageIds.current.add(id);
    setUsageById(prev => ({ ...prev, [id]: { status: 'loading' } }));
    api.get(`/Textblock/${id}/Usages`)
      .then(res => setUsageById(prev => ({ ...prev, [id]: { status: 'loaded', data: res.data } })))
      .catch(() => {
        // Allow a retry on the next selection.
        requestedUsageIds.current.delete(id);
        setUsageById(prev => ({ ...prev, [id]: { status: 'failed' } }));
      });
  }, []);

  const reloadTextblocks = useCallback(() => {
    api
      .get(`/Textblock/GetAllNames?language=DE`)
      .then((response) => {
        setTextblocks(response.data);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    reloadTextblocks();
  }, [reloadTextblocks]);

  const editor = useEditor({
    extensions: [
      Document,
      Paragraph,
      Text,
      BulletList,
      ListItem,
      Bold,
      Italic,
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: 'https'
      }),
      Underline,
      History,
      Color,
      TextStyle,
      HardBreak
    ],
    content: ""
  });

  const setLink = useCallback(() => {
    const previousUrl = editor.getAttributes('link').href
    const url = window.prompt('URL', previousUrl)

    // cancelled
    if (url === null) {
      return
    }

    // empty
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink()
        .run()

      return
    }

    // update link
    editor.chain().focus().extendMarkRange('link').setLink({ href: url })
      .run()
  }, [editor])

  // Saved state of the empty editor (TipTap normalizes "" to an empty paragraph).
  useEffect(() => {
    if (editor && bodyBaselineRef.current == null) bodyBaselineRef.current = editor.getHTML();
  }, [editor]);

  useEffect(() => {
    if (canHaveVariants) ensureLocations();
  }, [canHaveVariants, ensureLocations]);

  if (!editor) {
    return null
  }

  const isVariantTab = activeTab !== DEFAULT_TAB;
  const isNewVariantTab = isVariantTab && newVariant?.locationId === activeTab;

  // Puts a text into the editor and remembers it as the saved state of the open tab.
  const loadIntoEditor = (subject, body) => {
    editor.commands.setContent(body ?? "");
    bodyBaselineRef.current = editor.getHTML();
    subjectBaselineRef.current = subject ?? "";
    setVariantSubject(subject ?? "");
  };

  // Unsaved changes of the open tab: the text (default: subject/name are saved in the "Bearbeiten" popup),
  // for a variant also its subject; a new variant counts as unsaved until "Speichern".
  const isDirty = () => {
    const bodyChanged = bodyBaselineRef.current != null && editor.getHTML() !== bodyBaselineRef.current;
    if (!isVariantTab) return bodyChanged;
    return isNewVariantTab || bodyChanged || variantSubject !== subjectBaselineRef.current;
  };
  const dirty = isDirty();

  // Runs `action` right away, or after "Verwerfen" when the open tab has unsaved changes.
  const guardUnsaved = (action) => {
    if (!isDirty()) {
      action();
      return;
    }
    pendingActionRef.current = action;
    openPopup("textblockdiscardchanges");
  };

  const handleConfirmDiscard = () => {
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    // An action that opens the next popup replaces this one; closing afterwards would hide it again.
    if (action?.() !== OPENS_POPUP) closePopup();
  };

  const openTextblock = (tb) => {
    setSelected(tb);
    setSelectedTextBlockName(tb.name ?? "");
    setSelectedTextBlockFrontendName(tb.frontendName ?? "");
    setSelectedTextBlockSubject(tb.subject ?? "");
    setSelectedSendScope(normalizeScope(tb.sendScope));
    setActiveTab(DEFAULT_TAB);
    setNewVariant(null);
    loadIntoEditor(tb.subject, tb.body);
    loadUsage(tb.id);
  };

  // Dropdown liefert die ID als string. GetAllNames hat bereits ALLE Felder (inkl. body und Varianten),
  // ein zusätzlicher GetByName-Call ist deshalb nicht nötig.
  const handleDropdownChange = (event) => {
    const id = event.target.value;
    if (!id) {
      handleResetClick();
      return;
    }
    const tb = textblocks.find(t => String(t.id) === String(id));
    if (!tb) return;
    // The dropdown keeps showing the open block until the switch is confirmed.
    if (isDirty()) setDropdownKey(k => k + 1);
    guardUnsaved(() => openTextblock(tb));
  };

  const handleResetClick = () => {
    setSelected(null);
    setSelectedTextBlockName("");
    setSelectedTextBlockFrontendName("");
    setSelectedTextBlockSubject("");
    setSelectedSendScope(SEND_SCOPE.Both);
    setActiveTab(DEFAULT_TAB);
    setNewVariant(null);
    loadIntoEditor("", "");
  };

  // ── Location variants ────────────────────────────────────────────────────────────────────────────
  const variantTabs = canHaveVariants
    ? [
        { key: DEFAULT_TAB, label: "Default" },
        ...textblockVariants(selected).map(v => ({ key: v.locationId, label: v.locationName })),
        ...(newVariant ? [{ key: newVariant.locationId, label: newVariant.locationName, isNew: true }] : []),
      ]
    : [];
  const activeTabLabel = variantTabs.find(t => t.key === activeTab)?.label ?? "Default";
  const freeLocations = canHaveVariants ? locationsWithoutVariant(selected, locations) : [];
  const pickerValue = freeLocations.some(l => String(l.id) === pickerLocationId)
    ? pickerLocationId
    : String(freeLocations[0]?.id ?? "");

  // Opens a tab with its saved text (dropping an unsaved new variant).
  const showTab = (key) => {
    const content = key === DEFAULT_TAB ? selected : findTextblockVariant(selected, key);
    setNewVariant(null);
    setActiveTab(key);
    loadIntoEditor(content?.subject, content?.body);
  };

  const handleTabSelect = (key) => {
    if (key === activeTab) return;
    guardUnsaved(() => showTab(key));
  };

  const handleAddVariantClick = () => {
    const wasDirty = isDirty();
    guardUnsaved(() => {
      // "Verwerfen" confirmed: the open tab goes back to its saved text even if the picker is cancelled.
      if (wasDirty) showTab(isNewVariantTab ? DEFAULT_TAB : activeTab);
      ensureLocations();
      setPickerLocationId("");
      openPopup("addtextblockvariant");
      return OPENS_POPUP;
    });
  };

  const handleConfirmAddVariant = () => {
    const location = freeLocations.find(l => String(l.id) === pickerValue);
    if (!selected || !location) return;
    setNewVariant({ locationId: location.id, locationName: location.name });
    setActiveTab(location.id);
    // Starts as a copy of the default text; stays "neu" (unsaved) until "Speichern".
    loadIntoEditor(selected.subject, selected.body);
  };

  // Server state of a block (variant save/delete) → list + open block, without reloading.
  const applyTextblock = (tb) => {
    if (!tb) return;
    setTextblocks(prev => prev.map(t => (t.id === tb.id ? tb : t)));
    setSelected(prev => (prev?.id === tb.id ? tb : prev));
  };

  const handleSaveVariant = () => {
    if (!selected || !isVariantTab) return;
    if (!variantSubject.trim()) {
      setError("Bitte gib einen Betreff für die Variante ein.");
      openPopup("textblockrerror", 1);
      return;
    }
    const textblockId = selected.id;
    const locationId = activeTab;
    const label = activeTabLabel;
    const subject = variantSubject;
    const body = editor.getHTML();
    startSuccessBarLoading("Variante wird gespeichert");
    api
      .put(`/Textblock/${textblockId}/Variants/${locationId}`, { subject, body })
      .then((response) => {
        invalidateAddOns();
        startSuccessBarSuccess(`Variante für ${label} wurde gespeichert`);
        applyTextblock(response.data);
        const open = openRef.current;
        if (open.textblockId === textblockId && open.tab === locationId) {
          bodyBaselineRef.current = body;
          subjectBaselineRef.current = subject;
          setNewVariant(null);
        }
      })
      .catch((err) => {
        closeSuccessBar();
        setError(apiErrorText(err, 'Speichern fehlgeschlagen.'));
        openPopup("textblockrerror", 1);
      });
  };

  const handleDeleteVariantClick = () => {
    // A new variant is not saved yet — "Verwerfen" just drops it.
    if (isNewVariantTab) {
      showTab(DEFAULT_TAB);
      return;
    }
    openPopup("warningdeletetextblockvariant");
  };

  const handleConfirmDeleteVariant = () => {
    if (!selected || !isVariantTab) return;
    const textblockId = selected.id;
    const locationId = activeTab;
    const label = activeTabLabel;
    startSuccessBarLoading("Variante wird gelöscht");
    api
      .delete(`/Textblock/${textblockId}/Variants/${locationId}`)
      .then((response) => {
        invalidateAddOns();
        startSuccessBarSuccess(`Variante für ${label} wurde gelöscht`);
        applyTextblock(response.data);
        const open = openRef.current;
        if (open.textblockId === textblockId && open.tab === locationId) {
          setActiveTab(DEFAULT_TAB);
          loadIntoEditor(response.data?.subject, response.data?.body);
        }
      })
      .catch((err) => {
        closeSuccessBar();
        setError(apiErrorText(err, 'Löschen fehlgeschlagen.'));
        openPopup("textblockrerror", 1);
      });
  };

  const handleCreateClick = () => {
    setPopupName("");
    setPopupFrontendName("");
    setPopupSubject("");
    setPopupSendScope(SEND_SCOPE.Both);
    openPopup("createtextblock");
  };

  const handleCreateTextblockConfirm = () => {
    startSuccessBarLoading("Textbaustein wird erstellt");
    const body = {
      name: popupName,
      frontendName: popupFrontendName,
      subject: popupSubject,
      body: editor.getHTML(),
      language: "DE",
      sendScope: popupSendScope
    };

    api
      .post(`/Textblock/Create`, body)
      .then(() => {
        invalidateAddOns();
        startSuccessBarSuccess("Textbaustein wurde erstellt");
        handleResetClick();
        reloadTextblocks();
      })
      .catch((err) => {
        closeSuccessBar();
        setError(apiErrorText(err, 'Erstellen fehlgeschlagen.'));
        openPopup("textblockrerror", 1);
      });
  };

  const handleDeleteClick = () => {
    openPopup("warningdeletetextblock");
  };

  const handleConfirmDeleteTextblock = () => {
    if (!selected) return;
    startSuccessBarLoading("Textbaustein wird gelöscht");
    api
      .delete(`/Textblock/Delete/${selected.id}`)
      .then(() => {
        invalidateAddOns();
        startSuccessBarSuccess("Textbaustein wurde gelöscht");
        handleResetClick();
        reloadTextblocks();
      })
      .catch((err) => {
        closeSuccessBar();
        // A block that is still assigned is refused with the list of its usages — shown as is.
        setError(apiErrorText(err, 'Löschen fehlgeschlagen.'));
        openPopup("textblockrerror", 1);
      });
  };

  const handleEditClick = () => {
    openPopup("edittextblock");
  };

  const handleConfirmEditTextblock = () => {
    if (!selected) return;
    startSuccessBarLoading("Textbaustein wird angepasst");
    const body = {
      // System blocks: the internal name is fixed (the code sends them by name).
      name: selected.isSystemBlock ? null : selectedTextBlockName,
      frontendName: selectedTextBlockFrontendName,
      subject: selectedTextBlockSubject,
      body: editor.getHTML(),
      language: "DE",
      sendScope: selected.isSystemBlock ? null : selectedSendScope
    };

    api
      .put(`/Textblock/Update/${selected.id}`, body)
      .then(() => {
        invalidateAddOns();
        startSuccessBarSuccess("Textbaustein wurde angepasst");
        handleResetClick();
        reloadTextblocks();
      })
      .catch((err) => {
        closeSuccessBar();
        setError(apiErrorText(err, 'Speichern fehlgeschlagen.'));
        openPopup("textblockrerror", 1);
      });
  };

  const usage = selected ? usageById[selected.id] : null;
  // Placeholders of the mail types the block is used for (union); null = general list.
  const { labels: servedLabels, keys: slotPlaceholders } = placeholderScope(slots, usage?.status === 'loaded' ? usage.data : null);
  const editScopes = EDITABLE_SCOPES;
  // Rules/steps that still point to the block (warned before deleting it), without repeated entries.
  const usageReferences = usage?.status === 'loaded'
    ? [...new Set([...(usage.data?.participantGroupRules ?? []), ...(usage.data?.templateRules ?? []), ...(usage.data?.templateSteps ?? [])])]
    : [];

  return (
    <React.Fragment>
      <main className={"defaultExtendedMain"}>
        <OfflineBanner />
        <div className={"innerContainerTextblockEditor"}>
          <p className={"innerContainerLabel"}>Textbaustein auswählen</p>
          <CustomDropdown key={dropdownKey} value={selected ? String(selected.id) : ""} onChange={handleDropdownChange} icon={"message"}>
            <option value="" disabled hidden></option>
            {textblocks.map((textblock) => (
              <option key={textblock.id} value={textblock.id}>{textblock.frontendName}</option>
            ))}
          </CustomDropdown>
          {selected && <TextblockUsage usage={usage} textblock={selected} />}
          <p className={"innerContainerLabel"}>Bearbeiten</p>
          {canHaveVariants && (
            <div className={"textblockVariantSection"}>
              <TextblockVariantTabs
                tabs={variantTabs}
                activeKey={activeTab}
                dirty={dirty}
                onSelect={handleTabSelect}
                onAdd={handleAddVariantClick}
              />
              <p className={"textblockVariantHint"}>
                Varianten werden automatisch für Spieltage an diesem Ort verwendet; sonst gilt der Default.
              </p>
            </div>
          )}
          {selected?.isSystemBlock && (
            <div className={"textblockVariantSection"}>
              <p className={"textblockVariantHint"}>Systembausteine haben keine Orts-Varianten.</p>
            </div>
          )}
          <div
            className={"textblockVariantPanel"}
            id={"textblockVariantPanel"}
            role={canHaveVariants ? "tabpanel" : undefined}
            aria-labelledby={canHaveVariants ? `textblockVariantTab-${activeTab}` : undefined}
          >
            {/* Always-present slot: tippy moves the BubbleMenu element out of this panel, so React must never
                use it as the insert anchor for a conditionally rendered sibling (insertBefore crash). */}
            <div className={"textblockVariantSubjectSlot"}>
              {isVariantTab && (
                <Textfield
                  icon={"subject"}
                  type={"text"}
                  name={"variantSubject"}
                  label={`Betreff (${activeTabLabel})`}
                  placeholder={"Betreff"}
                  value={variantSubject}
                  onChange={(event) => setVariantSubject(event.target.value)}
                />
              )}
            </div>
            <BubbleMenu editor={editor}>
              <div className={"bubbleMenu"}>
                <Tooltip text="Rückgängig">
                  <button onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}>
                    <Icon className={"bubbleIcon"} icon={"undo"} />
                  </button>
                </Tooltip>
                <Tooltip text="Wiederholen">
                  <button onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}>
                    <Icon className={"bubbleIcon"} icon={"redo"} />
                  </button>
                </Tooltip>
                <Tooltip text="Fett">
                  <button
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    className={editor.isActive("bold") ? "isActive" : ""}
                  >
                    <Icon className={"bubbleIcon"} icon={"format_bold"} />
                  </button>
                </Tooltip>
                <Tooltip text="Kursiv">
                  <button
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    className={editor.isActive("italic") ? "isActive" : ""}
                  >
                    <Icon className={"bubbleIcon"} icon={"format_italic"} />
                  </button>
                </Tooltip>
                <Tooltip text="Link">
                  <button
                    onClick={setLink}
                    className={editor.isActive('link') ? 'is-active' : ''}
                  >
                    <Icon className={"bubbleIcon"} icon={"link"} />
                  </button>
                </Tooltip>
                <Tooltip text="Liste">
                  <button
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    className={editor.isActive('bulletList') ? 'is-active' : ''}
                  >
                    <Icon className={"bubbleIcon"} icon={"format_list_bulleted"} />
                  </button>
                </Tooltip>
                <Tooltip text="Farbe">
                  <input
                    type="color"
                    onInput={event => editor.chain().focus().setColor(event.target.value).run()}
                    value={editor.getAttributes('textStyle').color}
                    data-testid="setColor"
                  />
                </Tooltip>
                <Tooltip text="Farbe zurücksetzen">
                  <button
                    onClick={() => editor.chain().focus().unsetColor().run()}
                    data-testid="unsetColor"
                  >
                    <Icon className={"bubbleIcon"} icon={"format_color_reset"} />
                  </button>
                </Tooltip>
              </div>
            </BubbleMenu>
            <EditorContent editor={editor} />
            <div className={"textblockEditorButtonContainer"}>
              {!selected && <ActionButton icon="add" label="Erstellen" type={2} onClick={handleCreateClick} />}
              {selected && !isVariantTab && <ActionButton icon="edit" label="Bearbeiten" onClick={handleEditClick} />}
              {selected && !isVariantTab && !selected.isSystemBlock && <ActionButton icon="delete_sweep" label="Löschen" type={1} onClick={handleDeleteClick} />}
              {!isVariantTab && <ActionButton icon="replay" label="Leeren" onClick={handleResetClick} />}
              {isVariantTab && <ActionButton icon="check" label="Speichern" type={3} onClick={handleSaveVariant} disabled={!dirty} />}
              {isNewVariantTab && <ActionButton icon="close" label="Verwerfen" onClick={handleDeleteVariantClick} />}
              {isVariantTab && !isNewVariantTab && (
                <ActionButton icon="delete" label="Variante löschen" type={1} onClick={handleDeleteVariantClick} />
              )}
            </div>
          </div>
          <p className={"innerContainerLabel"}>Verfügbare Platzhalter</p>
          {slotPlaceholders && (
            <p className="textblockPlaceholderHint">
              Platzhalter für {servedLabels.join(', ')}
            </p>
          )}
          <TextblockEditorPlaceholder keys={slotPlaceholders} />
        </div>
      </main>
      <Popup name='createtextblock' confirmText={'Übernehmen'} onConfirm={handleCreateTextblockConfirm} withCancel type="info">
        <PopupDescriptionItem>Bitte fülle die restlichen Felder aus.</PopupDescriptionItem>
        <input type='text' className={"textblockPopupInput"} placeholder='Name' onChange={(event) => {setPopupName(event.target.value)}} value={popupName} />
        <PopupDescriptionItem>Der Name wird später ausschließlich für technische zwecke gebraucht. Beispiel wäre "confirmation"</PopupDescriptionItem>
        <input type='text' className={"textblockPopupInput"} placeholder='Frontend Name' onChange={(event) => {setPopupFrontendName(event.target.value)}} value={popupFrontendName} />
        <PopupDescriptionItem>Der Frontend Name wird im Dropdown angezeigt. Beispiel wäre "Bestätigungsmail"</PopupDescriptionItem>
        <input type='text' className={"textblockPopupInput"} placeholder='Titel' onChange={(event) => {setPopupSubject(event.target.value)}} value={popupSubject} />
        <PopupDescriptionItem>Der Titel der Email. Beispiel wäre "Spieltag Bestätigung für {'{Datum}'}"</PopupDescriptionItem>
        <CustomDropdown value={popupSendScope} onChange={(e) => setPopupSendScope(e.target.value)} icon={"send"}>
          {EDITABLE_SCOPES.map((scope) => (
            <option key={scope} value={scope}>{SCOPE_LABELS[scope]}</option>
          ))}
        </CustomDropdown>
        <PopupDescriptionItem>Wann darf dieser Baustein gesendet werden? Steuert, in welchem Mail-Dialog er auswählbar ist.</PopupDescriptionItem>
        <TextblockEditorPlaceholder />
      </Popup>
      <Popup name='edittextblock' confirmText={'Übernehmen'} onConfirm={handleConfirmEditTextblock} withCancel type="info">
        <PopupDescriptionItem>Bitte fülle die restlichen Felder aus.</PopupDescriptionItem>
        {selected?.isSystemBlock ? (
          <React.Fragment>
            <input type='text' className={"textblockPopupInput"} value={selectedTextBlockName} aria-label="Interner Name" readOnly disabled />
            <PopupDescriptionItem>Der interne Name von Systembausteinen ist fest vorgegeben – das System versendet sie darüber (z. B. Login-Code, Newsletter-Bestätigung).</PopupDescriptionItem>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <input type='text' className={"textblockPopupInput"} placeholder='Name' onChange={(event) => {setSelectedTextBlockName(event.target.value)}} value={selectedTextBlockName} />
            <PopupDescriptionItem>Der Name wird später ausschließlich für technische zwecke gebraucht. Beispiel wäre "confirmation"</PopupDescriptionItem>
          </React.Fragment>
        )}
        <input type='text' className={"textblockPopupInput"} placeholder='Frontend Name' onChange={(event) => {setSelectedTextBlockFrontendName(event.target.value)}} value={selectedTextBlockFrontendName} />
        <PopupDescriptionItem>Der Frontend Name wird im Dropdown angezeigt. Beispiel wäre "Bestätigungsmail"</PopupDescriptionItem>
        <input type='text' className={"textblockPopupInput"} placeholder='Titel' onChange={(event) => {setSelectedTextBlockSubject(event.target.value)}} value={selectedTextBlockSubject} />
        <PopupDescriptionItem>Der Titel der Email. Beispiel wäre "Spieltag Bestätigung für {'{Datum}'}"</PopupDescriptionItem>
        {selected?.isSystemBlock ? (
          <React.Fragment>
            <input type='text' className={"textblockPopupInput"} value={SCOPE_LABELS[normalizeScope(selectedSendScope)]} readOnly disabled />
            <PopupDescriptionItem>Sende-Regel von Systembausteinen ist fest vorgegeben und kann nicht geändert werden.</PopupDescriptionItem>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <CustomDropdown value={selectedSendScope} onChange={(e) => setSelectedSendScope(e.target.value)} icon={"send"}>
              {editScopes.map((scope) => (
                <option key={scope} value={scope}>{SCOPE_LABELS[scope]}</option>
              ))}
            </CustomDropdown>
            <PopupDescriptionItem>Wann darf dieser Baustein gesendet werden? Steuert, in welchem Mail-Dialog er auswählbar ist.</PopupDescriptionItem>
          </React.Fragment>
        )}
        <TextblockEditorPlaceholder keys={slotPlaceholders} />
      </Popup>
      <Popup name='textblockrerror' confirmText='Ok' headerText='Error' type="error">
        <p>{error}</p>
      </Popup>
      <Popup name='warningdeletetextblock' confirmText='Bestätigen' headerText='Warnung'
        type="warning" onConfirm={handleConfirmDeleteTextblock} withCancel
      >
        <p>Möchtest du den Textbaustein "{selectedTextBlockName}" wirklich dauerhaft löschen?</p>
        {/* Variants are deleted with the textblock (cascade) — say so before confirming. */}
        {textblockVariants(selected).length > 0 && (
          <p>
            Seine Varianten werden mitgelöscht: {textblockVariants(selected).map(v => v.locationName).join(', ')}.
          </p>
        )}
        {usageReferences.length > 0 && (
          <p>
            Er ist noch zugeordnet: {usageReferences.join(', ')}. Löschen ist erst möglich, wenn diese
            Zuordnungen entfernt sind.
          </p>
        )}
      </Popup>
      <Popup name='textblockdiscardchanges' confirmText='Verwerfen' headerText='Ungespeicherte Änderungen'
        type="warning" onConfirm={handleConfirmDiscard} dontCloseOnConfirm withCancel
      >
        <p>
          {isNewVariantTab
            ? `Die neue Variante für „${activeTabLabel}“ ist noch nicht gespeichert.`
            : isVariantTab
              ? `Die Änderungen an der Variante für „${activeTabLabel}“ sind noch nicht gespeichert.`
              : 'Die Änderungen am Text sind noch nicht gespeichert.'}
          {' '}Möchtest du sie verwerfen?
        </p>
      </Popup>
      <Popup name='addtextblockvariant' confirmText='Variante anlegen' headerText='Variante hinzufügen'
        onConfirm={handleConfirmAddVariant} withCancel withConfirm={freeLocations.length > 0} type="info"
      >
        {freeLocations.length > 0 ? (
          <React.Fragment>
            <PopupDescriptionItem>
              Für welchen Ort bekommt „{selected?.frontendName}“ einen eigenen Text? Die Variante startet als
              Kopie des Defaults und wird erst mit „Speichern“ angelegt.
            </PopupDescriptionItem>
            <CustomDropdown value={pickerValue} onChange={(event) => setPickerLocationId(event.target.value)} icon={"location_on"}>
              {freeLocations.map((location) => (
                <option key={location.id} value={String(location.id)}>{location.name}</option>
              ))}
            </CustomDropdown>
          </React.Fragment>
        ) : (
          <PopupDescriptionItem>
            {locationsFailed
              ? 'Die Orte konnten nicht geladen werden. Bitte später erneut versuchen.'
              : !locationsLoaded
                ? 'Orte werden geladen …'
                : locations.length === 0
                  ? 'Es sind noch keine Orte angelegt – das geht auf der Seite „Locations“ (Startseite → Verwaltung).'
                  : 'Für jeden Ort gibt es schon eine Variante.'}
          </PopupDescriptionItem>
        )}
      </Popup>
      <Popup name='warningdeletetextblockvariant' confirmText='Bestätigen' headerText='Warnung'
        type="warning" onConfirm={handleConfirmDeleteVariant} withCancel
      >
        <p>
          Möchtest du die Variante für „{activeTabLabel}“ wirklich löschen? Spieltage an diesem Ort bekommen
          danach wieder den Default-Text.
        </p>
      </Popup>
    </React.Fragment>
  )
}

export default TextblockEditorScreen
